import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Paper,
} from "@mui/material";
import StepOne from "./components/StepOne";
import StepTwo from "./components/StepTwo";
import Navbar from "../../../components/Navbar";
import { useLocation, useNavigate } from "react-router-dom";
import StepThree from "./components/StepThree";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Gobutton from "../../../components/Gobutton";
import { useDocs } from "../../../hooks/useDocs";
import { useAuth } from "../../../hooks/useAuth";
import ChooseBubbleType from "./components/ChooseBubbleType";
import { emitToast } from "common/toastEmitter";

const createBubbleSteps = [
  "Choose Bubble",
  "Create Bubble",
  "Create Content",
  "Create Library",
];

const editBubbleSteps = [
  "Edit Bubble",
  "Edit Content",
  "Edit Library",
];

export default function BubbleEditorPage() {
  const emptyBubble = {
    id: Date.now().toString(),
    title: "",
    lightColor: "",
    darkColor: "",
    activeColor: "",
    deactiveColor: "",
    size: 100,
    top: 0,
    left: 0,
    index: 0,
    content: { title: "", description: "", library: [] },
  };

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(emptyBubble);
  const { addBubble,updateExperienceWithBubbles } = useDocs();
  const { user } = useAuth();
  const [bubbleTypeId, setBubbleTypeId] = useState();
  const [steps, setSteps] = useState(createBubbleSteps);
  const location = useLocation();

  // pull editableAsset from navigation state
  const { editableBubble } = location.state || {};
  const isEditMode = Boolean(editableBubble);
  const navigate = useNavigate();

  // set asset when editing
  useEffect(() => {
    if (editableBubble && editableBubble.id) {
      setSteps(editBubbleSteps);
      setFormData(editableBubble);
    } else {
      setSteps(createBubbleSteps);
    }
  }, [editableBubble]);

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      if (editableBubble) {
      const { content, ...bubbleFields } = formData;
      const {library,...contentFields} = content;
       updateExperienceWithBubbles(
        editableBubble.id, // bubbleId
        editableBubble.experienceId, // experienceId
        bubbleFields, // bubble-level fields
        contentFields, // content object
        library || [], // libraries array
        user // pass user for ownership tracking
      );
      emitToast("Bubble Updated Succcesfully.", "success");
      } else {
        addBubble(bubbleTypeId, formData, user)
        emitToast("New Bubble Created Succcesfully.", "success");
      }
      navigate("/change-bubbles")
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

 const handleChange = (field, value) => {
  let newValue = value;

  if (["top", "left", "size"].includes(field)) {
    // Convert to number, fallback to 0 if invalid
    newValue = Number(value) || 0;
  }

  setFormData((prev) => ({ ...prev, [field]: newValue }));
};


  const handleContentChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      content: { ...prev.content, [field]: value },
    }));
  };

  return (
    <Box className="">
      <Navbar />
      <Box className="container">
        <Box className="flex justify-between mb-4">
          <Typography variant="h6">
            {!isEditMode ? "Create Bubble" : "Edit Bubble"}
          </Typography>
          <Gobutton
            text={"Back to List"}
            endIcon={<ArrowForwardIcon />}
            url={{ type: "assets", url: "/change-bubbles" }}
          />
        </Box>
        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel className="mb-3">
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: 4,
            gap: 2,
          }}
          elevation={4}
        >
          {!editableBubble ? (
            <>
              {activeStep === 0 && (
                <ChooseBubbleType bubble={bubbleTypeId} setBubble={setBubbleTypeId} />
              )}
              {/* Step 1: Bubble fields */}
              {activeStep === 1 && (
                <StepOne handleChange={handleChange} formData={formData} />
              )}

              {/* Step 2: Content */}
              {activeStep === 2 && (
                <StepTwo
                  handleContentChange={handleContentChange}
                  formData={formData}
                />
              )}

              {/* Step 3: Library */}
              {activeStep === 3 && (
                <StepThree setFormData={setFormData} formData={formData} />
              )}
            </>
          ) : (
            <>
              {/* Step 1: Bubble fields */}
              {activeStep === 0 && (
                <StepOne handleChange={handleChange} formData={formData} />
              )}

              {/* Step 2: Content */}
              {activeStep === 1 && (
                <StepTwo
                  handleContentChange={handleContentChange}
                  formData={formData}
                />
              )}

              {/* Step 3: Library */}
              {activeStep === 2 && (
                <StepThree setFormData={setFormData} formData={formData} />
              )}
            </>
          )}
        </Paper>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button onClick={handleBack} disabled={activeStep === 0}>
            Back
          </Button>
          <Button variant="contained" onClick={handleNext} sx={{ ml: 1 }}>
            {activeStep === steps.length - 1 ? "Save" : "Next"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
